import React, { createContext, useContext, useRef, useCallback } from 'react';

const ChartCacheContext = createContext(undefined);

const CACHE_TTL = 60 * 60 * 1000; // 1 hora em milissegundos

/**
 * Provider de cache para dados de gráficos.
 * Armazena dados em memória (useRef) para evitar re-renders.
 * Sobrevive navegação entre páginas enquanto o app estiver montado.
 * TTL de 1 hora — dados expirados são descartados automaticamente.
 */
export function ChartCacheProvider({ children }) {
  // { [cacheKey]: { data, timestamp } }
  const cacheRef = useRef({});
  // Estado da UI do ChartWrapper (preset, datas) para restaurar ao voltar
  const uiStateRef = useRef(null);

  const getCachedData = useCallback((key) => {
    const entry = cacheRef.current[key];
    if (!entry) return null;
    if (Date.now() - entry.timestamp > CACHE_TTL) {
      delete cacheRef.current[key];
      return null;
    }
    return entry.data;
  }, []);

  const setCachedData = useCallback((key, data) => {
    cacheRef.current[key] = { data, timestamp: Date.now() };
  }, []);

  const getUIState = useCallback(() => uiStateRef.current, []);

  const setUIState = useCallback((state) => {
    uiStateRef.current = state;
  }, []);

  const value = { getCachedData, setCachedData, getUIState, setUIState };

  return (
    <ChartCacheContext.Provider value={value}>
      {children}
    </ChartCacheContext.Provider>
  );
}

export function useChartCache() {
  const ctx = useContext(ChartCacheContext);
  if (ctx === undefined) {
    throw new Error('useChartCache must be used within a ChartCacheProvider');
  }
  return ctx;
}
